import { effect, signal } from '@preact/signals';
import { services } from '../app';
import { APP_NAME } from '../data/brand';
import { N } from '../data/guide';
import { LESSONS } from '../data/lessons';
import { aligning } from '../live/align';
import { lessonId } from '../live/learn';
import { panel, previewFrom, reliving, selection, selKey, storyShown, timeLive, viewMode, whatToShow, type Panel, type Selection } from '../state';
import { darkCheckOpen } from './DarkCheck';

// The last few things someone looked at, named as Ahillah names them, so feedback can ask what it is about without
// anyone needing the name of a page (the owner, 24 October 2026). Kept in memory only, never saved; sent only with a
// message, and then only as the names.

export interface Seen {
  /** For grouping messages: 'sky:ar', 'panel:tonight', 'sel:…'. */
  key: string;
  /** As the person sees it: "The sky, pointing your phone", "Tonight", "Moon". */
  label: string;
  icon: string;
}

/** What each sheet is called, by its title or the button that opens it. Help, More, feedback itself and the owner's page
 *  are ways to get somewhere, not where a problem shows: they are never what feedback is about. */
const PANELS: Partial<Record<Exclude<Panel, null>, [string, string]>> = {
  search: [N.search, 'search'],
  tonight: [N.tonight, 'tonight'],
  layers: [N.layers, 'layers'],
  settings: [N.settings, 'gear'],
  location: ['Choosing your place', 'pin'],
  time: ['Changing the time', 'clock'],
  tour: ['Guided tour', 'play'],
  go: ['Find darker skies', 'pin'],
  hunt: ['Sky Hunt', 'target'],
  learn: ['Lessons and quizzes', 'star'],
  quiz: ['A quiz', 'check'],
  nights: [N.yourNights, 'nights'],
  night: [`A night in ${N.yourNights}`, 'nights'],
  whatsnew: ['What’s new', 'gift'],
  islam: ['The stars in Islam', 'book'],
  calendar: [N.skyCalendar, 'calendar'],
  gallery: [N.gallery, 'gallery'],
  walkthrough: ['Show me around', 'compass'],
  name: [`Why ${APP_NAME}?`, 'moon'],
  sizes: ['How big are they?', 'sizes'],
};
const NEVER = new Set<Panel>(['help', 'more', 'feedback', 'owner']);

function selIcon(sel: Selection): string {
  switch (sel.kind) {
    case 'body': return sel.id === 'Moon' ? 'moon' : sel.id === 'Sun' ? 'sun' : 'planet';
    case 'star': return 'star';
    case 'satellite': return 'satellite';
    case 'shower': return 'meteor';
    case 'small': return 'planet';
    case 'manzil': return 'moon';
    case 'constellation':
    case 'figure':
    case 'arabic': return 'draw';
    default: return 'sparkle';
  }
}

/** The sky itself, as it was being shown. */
function sky(): Seen {
  if (reliving.value) return { key: 'sky:relive', label: 'Reliving a night', icon: 'relive' };
  if (previewFrom.value) return { key: 'sky:elsewhere', label: 'The sky from another place', icon: 'pin' };
  if (!timeLive.value) return { key: 'sky:time', label: 'The sky at another time', icon: 'clock' };
  return viewMode.value === 'ar'
    ? { key: 'sky:ar', label: 'The sky, pointing your phone', icon: 'phone' }
    : { key: 'sky:manual', label: 'The sky, moving it with your finger', icon: 'hand' };
}

/** What is in front now, or null while Help, More or feedback is (they leave the list as it was). */
export function inFront(): Seen | null {
  if (storyShown.value) return { key: 'story', label: 'What’s new', icon: 'gift' };
  if (darkCheckOpen.value) return { key: 'darkcheck', label: N.darkCheck, icon: 'nightEye' };
  const p = panel.value;
  if (NEVER.has(p)) return null;
  // Waiting to be asked is not on screen: it shows only with no sheet or card open (src/ui/App.tsx).
  if (whatToShow.value && !p && !selection.value) return { key: 'whattoshow', label: 'What to show on the sky', icon: 'layers' };
  if (p === 'lesson') {
    const l = LESSONS.find((x) => x.id === lessonId.value);
    return { key: `lesson:${l?.id ?? ''}`, label: l ? `Lesson: ${l.title}` : 'A lesson', icon: 'star' };
  }
  if (p) {
    const [label, icon] = PANELS[p] ?? [p, 'info'];
    return { key: `panel:${p}`, label, icon };
  }
  if (aligning.value) return { key: 'align', label: 'Align with the sky', icon: 'target' };
  const sel = selection.value;
  if (sel) {
    let label = '';
    try {
      label = services.peek()?.engine.labelFor(sel) ?? '';
    } catch {
      /* not known to the sky now */
    }
    return { key: `sel:${selKey(sel)}`.replace(/[^\x20-\x7E]/g, '?').slice(0, 120), label: label.slice(0, 120) || 'Something you tapped on the sky', icon: selIcon(sel) };
  }
  return sky();
}

/** Newest first, at most six. */
export const trail = signal<Seen[]>([]);
export const TRAIL_MAX = 6;

/** Follow what is in front, from when Ahillah opens. Returns the clean-up. */
export function watchTrail(): () => void {
  return effect(() => {
    const now = inFront();
    if (!now) return;
    const was = trail.peek();
    if (was[0]?.key === now.key && was[0].label === now.label) return;
    trail.value = [now, ...was.filter((x) => x.key !== now.key)].slice(0, TRAIL_MAX);
  });
}
